import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.data instanceof FormData && config.headers) {
    delete config.headers["Content-Type"];
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Try to refresh token
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken && !error.config._retry) {
        error.config._retry = true;
        try {
          const formData = new FormData();
          formData.append("refresh_token", refreshToken);
          const response = await axios.post(
            `${API_BASE}/auth/refresh`,
            formData,
          );
          const newToken = response.data.access_token;
          localStorage.setItem("accessToken", newToken);
          error.config.headers.Authorization = `Bearer ${newToken}`;
          return api(error.config);
        } catch (refreshError) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          window.location.href = "/admin/login";
        }
      }
    }
    return Promise.reject(error);
  },
);

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    return response.data;
  },
  register: async (data) => {
    const response = await api.post("/auth/register", data);
    return response.data;
  },
  getMe: async () => {
    const response = await api.get("/auth/me");
    return response.data;
  },
};

// Posts API
export const postsAPI = {
  getAll: async (params = {}) => {
    const response = await api.get("/posts", { params });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/posts/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post("/posts", data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/posts/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/posts/${id}`);
    return response.data;
  },
};

// Adeverinta API
export const adeverintaAPI = {
  submit: async (data) => {
    const response = await api.post("/adeverinta", data);
    return response.data;
  },
  getAll: async (status = null) => {
    const params = status ? { status } : {};
    const response = await api.get("/adeverinta", { params });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/adeverinta/${id}`);
    return response.data;
  },
  updateStatus: async (id, status) => {
    const params = new URLSearchParams();
    params.append("new_status", status);
    const response = await api.put(`/adeverinta/${id}/status`, params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    return response.data;
  },
  uploadSigned: async (id, file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(`/adeverinta/${id}/sign`, formData);
    return response.data;
  },
  downloadPDF: async (id) => {
    const response = await api.get(`/adeverinta/${id}/download`, {
      responseType: "blob",
    });
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/adeverinta/${id}`);
    return response.data;
  },
};

// Prayer Times API
export const prayerTimesAPI = {
  get: async () => {
    const response = await api.get("/prayer-times");
    return response.data;
  },
};

// Islamic Dates API
export const islamicDatesAPI = {
  get: async () => {
    const response = await api.get("/islamic-dates");
    return response.data;
  },
  update: async (data) => {
    const response = await api.put("/islamic-dates", data);
    return response.data;
  },
};

// Islamic Events API
export const islamicEventsAPI = {
  getAll: async (activeOnly = false) => {
    const params = activeOnly ? { active_only: true } : {};
    const response = await api.get("/islamic-events", { params });
    return response.data;
  },
  create: async (data) => {
    const response = await api.post("/islamic-events", data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/islamic-events/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/islamic-events/${id}`);
    return response.data;
  },
};

// Settings API
export const settingsAPI = {
  get: async () => {
    const response = await api.get("/settings");
    return response.data;
  },
  update: async (data) => {
    const response = await api.put("/settings", data);
    return response.data;
  },
};

// Donations API
export const donationsAPI = {
  getAll: async () => {
    const response = await api.get("/donations");
    return response.data;
  },
  create: async (data) => {
    const response = await api.post("/donations", data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/donations/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/donations/${id}`);
    return response.data;
  },
};

// Users API
export const usersAPI = {
  getAll: async () => {
    const response = await api.get("/users");
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },
};

// Stats API
export const statsAPI = {
  get: async () => {
    const response = await api.get("/stats");
    return response.data;
  },
};

// Cloudinary API (mock)
export const cloudinaryAPI = {
  getSignature: async (resourceType = "image", folder = "uploads") => {
    const response = await api.get("/cloudinary/signature", {
      params: { resource_type: resourceType, folder },
    });
    return response.data;
  },
};

export default api;
